import { closestCorners, pointerWithin } from "@dnd-kit/core";
import { toNullableNumber } from "../../variantAxes";

function formatCatalogDeliveryDate(value) {
  const t = Date.parse(String(value));
  if (Number.isNaN(t)) return null;

  return new Date(t).toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
}

function deliveryDateFromProduct(product) {
  if (product == null || typeof product !== "object") return null;
  const raw = product.delivery_date;
  if (raw == null || String(raw).trim() === "") return null;

  return String(raw);
}

function deliveryDateFromAxisOption(axisOptions, selectedPid) {
  const pid = toNullableNumber(selectedPid);
  if (pid == null || !Array.isArray(axisOptions)) return null;

  const selected = axisOptions.find(
    (opt) => toNullableNumber(opt.product_id) === pid
  );
  if (selected == null || typeof selected !== "object") return null;

  return deliveryDateFromProduct(selected);
}

function latestDeliveryDate(candidates) {
  let best = null;
  let bestTime = Number.NEGATIVE_INFINITY;

  for (const candidate of candidates) {
    if (candidate == null || String(candidate).trim() === "") continue;
    const time = Date.parse(String(candidate));
    if (Number.isNaN(time)) continue;
    if (time > bestTime) {
      bestTime = time;
      best = String(candidate);
    }
  }

  return best;
}

/** Matches catalog `against_order`; works for merged project line payloads too. */
export function storeCatalogProductIsAgainstOrder(p) {
  return p != null && typeof p === "object" && p.against_order === true;
}

export function isSelectedDimensionNonStandard(axisOptions, selectedPid) {
  const pid = toNullableNumber(selectedPid);
  if (pid == null || !Array.isArray(axisOptions)) return false;
  const selected = axisOptions.find(
    (opt) => toNullableNumber(opt.product_id) === pid
  );
  return selected?.against_order === true;
}

function isStandardDeliveryVariant({
  currentProduct,
  heightOpts,
  widthOpts,
  depthOpts,
  heightPid,
  widthPid,
  depthPid,
}) {
  return (
    !storeCatalogProductIsAgainstOrder(currentProduct) &&
    !isSelectedDimensionNonStandard(heightOpts, heightPid) &&
    !isSelectedDimensionNonStandard(widthOpts, widthPid) &&
    !isSelectedDimensionNonStandard(depthOpts, depthPid)
  );
}

/** Delivery date label for catalog rows and project cart lines (API `delivery_date`). */
export function getDeliveryTextForSelectedVariant({
  currentProduct,
  heightOpts,
  widthOpts,
  depthOpts,
  heightPid,
  widthPid,
  depthPid,
  useTomorrowForStandard = false,
}) {
  if (
    useTomorrowForStandard &&
    isStandardDeliveryVariant({
      currentProduct,
      heightOpts,
      widthOpts,
      depthOpts,
      heightPid,
      widthPid,
      depthPid,
    })
  ) {
    return "Tomorrow";
  }

  const effectiveDate = latestDeliveryDate([
    deliveryDateFromProduct(currentProduct),
    deliveryDateFromAxisOption(heightOpts, heightPid),
    deliveryDateFromAxisOption(widthOpts, widthPid),
    deliveryDateFromAxisOption(depthOpts, depthPid),
  ]);
  const formatted = effectiveDate
    ? formatCatalogDeliveryDate(effectiveDate)
    : null;

  return formatted || "—";
}

export function collectFolderIdsFromTree(nodes, out = []) {
  if (!Array.isArray(nodes)) return out;
  for (const n of nodes) {
    if (n.item_type === "folder") {
      out.push(n.id);
      collectFolderIdsFromTree(n.children, out);
    }
  }
  return out;
}

export function getProjectItemProductId(item) {
  if (item == null || item.item_type !== "product") return null;
  const raw = item.product_id ?? null;
  if (raw === null || raw === undefined || raw === "") return null;
  const n = Number(raw);
  return Number.isNaN(n) ? null : n;
}

function mergeSiblingProductNodes(children) {
  if (!Array.isArray(children) || children.length === 0) return children;
  const out = [];
  const indexByProductId = new Map();

  for (const child of children) {
    if (child.item_type === "folder") {
      out.push(child);
      continue;
    }
    if (child.item_type === "product") {
      const pid = getProjectItemProductId(child);
      if (pid == null) {
        out.push(child);
        continue;
      }
      if (indexByProductId.has(pid)) {
        const idx = indexByProductId.get(pid);
        const agg = out[idx];
        const prevIds = agg._groupItemIds || [agg.id];
        agg._groupItemIds = [...prevIds, child.id];
        agg._groupQty = agg._groupItemIds.length;
      } else {
        indexByProductId.set(pid, out.length);
        out.push({
          ...child,
          product_id: pid,
          _groupQty: 1,
          _groupItemIds: [child.id],
        });
      }
    } else {
      out.push(child);
    }
  }
  return out;
}

export function mergeDuplicateProductRowsInTree(nodes) {
  if (!Array.isArray(nodes) || nodes.length === 0) return nodes;
  const mergedSiblings = mergeSiblingProductNodes(nodes);
  return mergedSiblings.map((node) => {
    if (node.item_type === "folder" && node.children?.length) {
      return {
        ...node,
        children: mergeDuplicateProductRowsInTree(node.children),
      };
    }
    return node;
  });
}

export function countProductUnitsInFolderSubtree(folderNode) {
  if (!folderNode?.children?.length) return 0;
  let n = 0;
  for (const child of folderNode.children) {
    if (child.item_type === "folder") {
      n += countProductUnitsInFolderSubtree(child);
    } else if (child.item_type === "product") {
      n += 1;
    }
  }
  return n;
}

function normalizeDimensionToken(s) {
  return String(s ?? "")
    .replace(/\u00a0/g, " ")
    .trim();
}

function getSizeParts(sizeValue) {
  if (sizeValue && typeof sizeValue === "object" && !Array.isArray(sizeValue)) {
    const h = sizeValue.height;
    const w = sizeValue.width;
    const d = sizeValue.depth;
    if (!Array.isArray(h) && !Array.isArray(w) && !Array.isArray(d)) {
      if (h !== undefined || w !== undefined || d !== undefined) {
        return {
          height: normalizeDimensionToken(h),
          width: normalizeDimensionToken(w),
          depth: normalizeDimensionToken(d),
        };
      }
    }
  }
  const raw = normalizeDimensionToken(sizeValue);
  if (!raw) return { height: "", width: "", depth: "" };
  const parts = raw
    .split(/[x×]/i)
    .map((s) => normalizeDimensionToken(s))
    .filter(Boolean);
  return {
    height: parts[0] ?? "",
    width: parts[1] ?? "",
    depth: parts[2] ?? "",
  };
}

export function formatSizeForDisplay(sizeValue) {
  const { height, width, depth } = getSizeParts(sizeValue);
  const parts = [height, width, depth]
    .map((x) => normalizeDimensionToken(x))
    .filter(Boolean);
  return parts.join(" × ");
}

export function isTouchUi() {
  if (typeof window === "undefined") return false;
  if (typeof navigator !== "undefined" && navigator.maxTouchPoints > 0)
    return true;
  if (window.matchMedia("(hover: none)").matches) return true;
  if (window.matchMedia("(pointer: coarse)").matches) return true;
  return false;
}

export const getProjectItemDisplayName = (item) => {
  if (item.item_type === "folder") {
    const n = item.name && String(item.name).trim();
    return n || `Folder #${item.id}`;
  }
  const name = item.name && String(item.name).trim();
  if (name) return name;
  if (item.product_id != null) return `Product #${item.product_id}`;
  return `Item #${item.id}`;
};

export const getProductThumbUrl = (item) => {
  const imgs = item.images;
  if (!Array.isArray(imgs) || imgs.length === 0) return null;
  const u = imgs[0];
  return u != null && String(u).trim() !== "" ? String(u).trim() : null;
};

export const toPriceNum = (raw) => {
  if (raw == null || raw === "") return null;
  const n = Number(raw);
  return Number.isNaN(n) ? null : n;
};

export const formatInr = (n) =>
  n == null ? null : `₹${n.toLocaleString("en-IN")}`;

/** Project line product pricing — API fields on normalized line item. */
export const getProductLinePricing = (
  item,
  { usePersistedPricesOnly = true } = {}
) => {
  const listNum = toPriceNum(item.price);
  const discNum = toPriceNum(item.discount_price);
  let listPriceText = null;
  let salePriceText = null;
  if (discNum != null && listNum != null && discNum !== listNum) {
    listPriceText = formatInr(listNum);
    salePriceText = formatInr(discNum);
  } else if (discNum != null) {
    salePriceText = formatInr(discNum);
  } else if (listNum != null) {
    salePriceText = formatInr(listNum);
  } else if (usePersistedPricesOnly) {
    // Placed projects should not fall back to any mutable product prices.
    return { listPriceText: null, salePriceText: null, unitText: null };
  }
  const sqft = item.sqft_price;
  const unitText =
    sqft != null && !Number.isNaN(Number(sqft))
      ? `₹${Number(sqft).toLocaleString("en-IN")} / sqft.`
      : null;
  return { listPriceText, salePriceText, unitText };
};

function pickSmallestDroppableAreaCollisions(args, collisions) {
  if (!collisions.length) return [];
  const scored = collisions
    .map((collision) => {
      const rect = args.droppableRects.get(collision.id);
      const area =
        rect && rect.width != null && rect.height != null
          ? rect.width * rect.height
          : Number.POSITIVE_INFINITY;
      return { collision, area };
    })
    .sort(
      (a, b) =>
        a.area - b.area ||
        String(a.collision.id).localeCompare(String(b.collision.id))
    );
  return [scored[0].collision];
}

/**
 * Prefer folder header/body drops over sibling reorder slots so folders do not
 * fight `ord-*` insert gaps while moving items into a folder.
 */
export function projectCartCollisionDetection(args) {
  const hits = pointerWithin(args);
  if (hits.length > 0) {
    const fldhHits = hits.filter((h) => String(h.id).startsWith("fldh-"));
    if (fldhHits.length > 0) {
      return pickSmallestDroppableAreaCollisions(args, fldhHits);
    }

    const ordHits = hits.filter((h) => String(h.id).startsWith("ord-"));
    if (ordHits.length > 0) {
      return pickSmallestDroppableAreaCollisions(args, ordHits);
    }

    const fldHits = hits.filter(
      (h) =>
        String(h.id).startsWith("fld-") && !String(h.id).startsWith("fldh-")
    );
    if (fldHits.length > 0) {
      return pickSmallestDroppableAreaCollisions(args, fldHits);
    }

    return pickSmallestDroppableAreaCollisions(args, hits);
  }
  return closestCorners(args);
}
